import { ShareLinkFile } from "../entities";
import { FileCard, ItemActivation, ThumbnailSource } from "./fileCard";

/** Gap between file cards, in pixels. Shared by the grid and its skeleton. */
export const FILE_GRID_GAP_PX = 35;

/**
 * Responsive grid track for the file cards. Caps each column at 174px but
 * never lets a column exceed `50% - halfGap`, which guarantees at least two
 * columns even on narrow (mobile) viewports.
 */
export const FILE_GRID_TEMPLATE_COLUMNS = `repeat(auto-fill, minmax(min(174px, calc(50% - ${
  FILE_GRID_GAP_PX / 2
}px)), 1fr))`;

export type FileGridProps = {
  files: ShareLinkFile[];
  selectedUids: Set<string>;
  onToggleSelect: (uid: string) => void;
  /** Invoked with the file when a card is activated to open its preview. */
  onOpenPreview: (file: ShareLinkFile) => void;
  /** How to source each card's thumbnail; forwarded to {@link FileCard}. */
  thumbnails?: ThumbnailSource;
  /** True while a marquee drag is active (suppresses card hover + pointer cursor). */
  selecting?: boolean;
  /** Click activation model (see {@link ItemActivation}). Defaults to `"preview"`. */
  activation?: ItemActivation;
  /** Plain-click exclusive select for `"select"` activation. */
  onSelectExclusive?: (uid: string) => void;
  /** Shift+click range select for `"select"` activation. */
  onSelectRange?: (uid: string) => void;
  /**
   * Keep every card's checkbox visible instead of revealing it on hover. Set on
   * touch devices, which have no hover to surface it.
   */
  checkboxAlwaysVisible?: boolean;
};

/**
 * Responsive grid of {@link FileCard}s — the default (wide-viewport) file
 * layout. Presentational: the selection state and the single, shared preview
 * overlay are owned by the parent; the grid only lays out cards and forwards
 * their callbacks.
 *
 * Cards carry the overlay checkbox (pinned to the thumbnail's top-right corner
 * and revealed on hover / selection), matching the published-space grid.
 * `selectionActive` is derived from the selection so a touch tap toggles rather
 * than opening the preview once anything is selected.
 */
export function FileGrid({
  files,
  selectedUids,
  onToggleSelect,
  onOpenPreview,
  thumbnails,
  selecting = false,
  activation = "preview",
  onSelectExclusive,
  onSelectRange,
  checkboxAlwaysVisible = false,
}: FileGridProps) {
  const selectionActive = selectedUids.size > 0;
  return (
    <div
      className="grid pb-6"
      style={{
        gridTemplateColumns: FILE_GRID_TEMPLATE_COLUMNS,
        gap: `${FILE_GRID_GAP_PX}px`,
      }}
    >
      {files.map((file) => (
        <FileCard
          key={file.uid}
          file={file}
          isSelected={selectedUids.has(file.uid)}
          onToggleSelect={onToggleSelect}
          onSelectExclusive={onSelectExclusive}
          onSelectRange={onSelectRange}
          onOpenPreview={onOpenPreview}
          thumbnails={thumbnails}
          selecting={selecting}
          activation={activation}
          selectionActive={selectionActive}
          checkboxPlacement="overlay"
          checkboxAlwaysVisible={checkboxAlwaysVisible}
        />
      ))}
    </div>
  );
}
